import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ComponentRef,
  inject,
  Input,
  Signal,
  Type,
  ViewChild,
  ViewContainerRef
} from '@angular/core';
import { MODAL_TOKEN, ModalProvider } from '../../tokens';

export interface ActionModalTemplate<T = unknown> {
  readonly selected: Signal<T | null>;
}

@Component({
  templateUrl: './action-modal.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true
})
export class ActionModalComponent implements AfterViewInit {
  @ViewChild('componentHost', { read: ViewContainerRef })
  componentHost!: ViewContainerRef;

  @Input() header!: string;
  @Input() message!: string;
  @Input() template!: Type<unknown>;
  @Input() templateInputs?: Record<string, unknown>;
  @Input() requireTemplateSelection?: boolean;
  @Input() btnOk!: string;
  @Input() btnCancel!: string;
  @Input() action!: (selected?: unknown) => void;
  @Input() cancelAction!: () => void;

  private readonly modalProvider: ModalProvider = inject(MODAL_TOKEN);
  private templateInstance?: Partial<ActionModalTemplate>;

  ngAfterViewInit(): void {
    if (this.template instanceof Type) {
      this.createComponent(this.template);
    }
  }

  get isOkDisabled(): boolean {
    return !!this.requireTemplateSelection && this.templateSelected() == null;
  }

  closeModal(): void {
    this.modalProvider.dismissAllStoredModals();
    this.cancelAction?.();
  }

  handle(): void {
    if (this.isOkDisabled) return;

    this.modalProvider.dismissAllStoredModals();
    this.action?.(this.templateSelected());
  }

  private templateSelected(): unknown {
    return this.templateInstance?.selected?.();
  }

  private createComponent(component: Type<unknown>): void {
    this.componentHost.clear();
    const componentRef: ComponentRef<unknown> = this.componentHost.createComponent(component);

    if (this.templateInputs) {
      Object.entries(this.templateInputs).forEach(([key, value]: [string, unknown]) =>
        componentRef.setInput(key, value)
      );
    }

    this.templateInstance = componentRef.instance as Partial<ActionModalTemplate>;
    componentRef.changeDetectorRef.detectChanges();
  }
}
